// PartsLaborRollup — Phase 4D + Phase 10 R2
// Grouped 5-section rollup for the unified Parts & Labor tab.
//
// Sections (R2 update):
//   1. Labor          — when financials ON: $sell from ticket_jobs labor sum
//                       when financials OFF: book minutes (legacy display)
//   2. Parts          — partsSubtotal from rollup
//   3. Core charges   — coreSubtotal from rollup
//   4. Fees & tax     — estFees + estTax from rollup
//   5. Estimated total (parts side)
//   + Subtotal (parts + labor) — only when financials ON
//
// DISPLAY-ONLY. It must not write, mutate, or feed into the scheduler.

import type { PartsRollupResult } from '@/lib/parts-financial/calculator';

const fmt = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

function formatMinutes(minutes: number): string {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
}

interface Props {
  /** Sum of ticket_jobs minutes (book) — display only, never authoritative for scheduling. */
  laborMinutes: number;
  /** Optional: Tickets.predicted_minutes for soft dev-only drift validation. */
  predictedMinutes?: number;
  /** Parts/financial rollup. Optional — section will read zeros if absent. */
  rollup?: PartsRollupResult | null;
  /** Phase 10 R2 — labor sell totals from useTicketLaborLines (only when financials ON). */
  laborTotals?: {
    total_labor_sell: number;
    computed_count: number;
    uncomputed_count: number;
  } | null;
  /** Phase 10 R2 — when true, render sell-side labor row + parts+labor subtotal. */
  showFinancials?: boolean;
  /** Phase 10 R3 — tech pay totals (only when ui_technician_pay ON + manager view). */
  techPayTotals?: {
    total_tech_pay_amount: number;
    computed_count: number;
    deferred_hourly_count: number;
    no_config_count: number;
    uncomputed_count: number;
  } | null;
  /** Phase 10 R3 — when true, render the tech-pay row (separate from sell). */
  showTechPay?: boolean;
}

export function PartsLaborRollup({
  laborMinutes,
  predictedMinutes,
  rollup,
  laborTotals,
  showFinancials,
  techPayTotals,
  showTechPay,
}: Props) {
  // --- Correction 4: Soft dev-only reconciliation (no throws, no UI errors) ---
  if (
    import.meta.env.DEV &&
    typeof predictedMinutes === 'number' &&
    laborMinutes !== predictedMinutes
  ) {
     
    console.warn('[Phase4 rollup drift]', {
      displayedLaborMinutes: laborMinutes,
      predictedMinutes,
    });
  }

  const partsSubtotal = rollup?.partsSubtotal ?? 0;
  const coreSubtotal = rollup?.coreSubtotal ?? 0;
  const estFees = rollup?.estFees ?? 0;
  const estTax = rollup?.estTax ?? 0;
  const estTotal = rollup?.estTotal ?? 0;
  const feesAndTax = Number((estFees + estTax).toFixed(2));

  const totalLaborSell = laborTotals?.total_labor_sell ?? 0;
  const uncomputed = laborTotals?.uncomputed_count ?? 0;
  const partsPlusLabor = Number((estTotal + totalLaborSell).toFixed(2));

  return (
    <div className="rounded-lg border bg-muted/20 p-4 space-y-2">
      <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
        <span>Parts &amp; Labor Estimate</span>
        <span className="opacity-70">Estimate · not final</span>
      </div>

      <div className="space-y-1 text-sm">
        {/* 1. Labor — sell dollars when financials ON, minutes-only when OFF */}
        {showFinancials ? (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">
              Labor (sold)
              <span className="ml-2 text-[11px] opacity-70">
                ({formatMinutes(laborMinutes)} scheduled
                {uncomputed > 0 ? ` · ${uncomputed} not computed` : ''})
              </span>
            </span>
            <span className="tabular-nums">{fmt(totalLaborSell)}</span>
          </div>
        ) : (
          <Row
            label="Labor (scheduled time)"
            value={formatMinutes(laborMinutes)}
            hint="Book minutes from jobs"
          />
        )}
        {/* 2. Parts */}
        <Row label="Parts subtotal" value={fmt(partsSubtotal)} />
        {/* 3. Core */}
        <Row label="Core charges" value={fmt(coreSubtotal)} />
        {/* 4. Fees & tax (combined) */}
        <Row label="Est. fees &amp; tax" value={fmt(feesAndTax)} />
      </div>

      {/* 5. Estimated total (parts side) */}
      <div className="border-t pt-2 mt-1 flex items-center justify-between">
        <span className="text-sm font-medium">Estimated total (parts side)</span>
        <span className="text-base font-semibold tabular-nums">{fmt(estTotal)}</span>
      </div>

      {/* Phase 10 R2 — combined subtotal (only when financials ON) */}
      {showFinancials && (
        <div className="flex items-center justify-between border-t pt-2">
          <span className="text-sm font-medium">Subtotal (parts + labor)</span>
          <span className="text-base font-semibold tabular-nums">{fmt(partsPlusLabor)}</span>
        </div>
      )}

      {/* Phase 10 R3 — tech pay total (manager-only, separate from sell). */}
      {showTechPay && techPayTotals && (
        <div className="border-t pt-2 mt-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              Tech pay (computed)
              {(techPayTotals.deferred_hourly_count > 0 ||
                techPayTotals.no_config_count > 0 ||
                techPayTotals.uncomputed_count > 0) && (
                <span className="ml-2 text-[11px] text-muted-foreground font-normal">
                  ({[
                    techPayTotals.deferred_hourly_count > 0 &&
                      `${techPayTotals.deferred_hourly_count} deferred (hourly)`,
                    techPayTotals.no_config_count > 0 &&
                      `${techPayTotals.no_config_count} no config`,
                    techPayTotals.uncomputed_count > 0 &&
                      `${techPayTotals.uncomputed_count} not computed`,
                  ]
                    .filter(Boolean)
                    .join(' · ')})
                </span>
              )}
            </span>
            <span className="tabular-nums">{fmt(techPayTotals.total_tech_pay_amount)}</span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Pay-side only — never blended into the parts + labor sell subtotal.
          </p>
        </div>
      )}

      {!showFinancials && (
        <p className="text-[11px] text-muted-foreground pt-1">
          Labor minutes are scheduling estimates and are not converted to dollars here.
        </p>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">
        {label}
        {hint && <span className="ml-2 text-[11px] opacity-70">({hint})</span>}
      </span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
